import React, { useState } from 'react';
import { useParams } from 'wouter';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Download, Eye, FileText, FolderOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface PackageItem {
  id: string;
  name: string;
  itemType: 'file' | 'folder';
  path: string;
  size?: number;
  versionId?: string;
}

interface PackageViewData {
  id: string;
  packageNumber: string;
  name: string;
  description?: string;
  createdAt: string;
  submissionDeadline?: string;
  items: PackageItem[];
  recipientCompany: string;
  sentAt: string;
  expiresAt: string;
}

const PackageView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');

  // Authenticate with email and access code
  const authMutation = useMutation({
    mutationFn: async ({ email, accessCode }: { email: string; accessCode: string }) => {
      const response = await fetch(`/api/package-view/${shareToken}/authenticate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, accessCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Authentication failed');
      }

      return response.json();
    },
    onSuccess: () => {
      setIsAuthenticated(true);
      setError('');
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Fetch package data after authentication
  const { data: packageData, isLoading: isLoadingPackage } = useQuery<PackageViewData>({
    queryKey: ['package-view', shareToken],
    queryFn: async () => {
      const response = await fetch(`/api/package-view/${shareToken}`, {
        headers: {
          'Authorization': `Bearer ${email}:${accessCode}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch package data');
      }

      return response.json();
    },
    enabled: isAuthenticated,
  });

  // Download package
  const downloadMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/package-view/${shareToken}/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${email}:${accessCode}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${packageData?.name || 'package'}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });

  const handleAuthenticate = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && accessCode) {
      authMutation.mutate({ email, accessCode });
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Authentication form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-900">
              Access Package
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Enter your email and company access code to view the package contents.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuthenticate} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="accessCode">8-Digit Access Code</Label>
                <Input
                  id="accessCode"
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="12345678"
                  maxLength={8}
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  This code was provided in your invitation email.
                </p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={authMutation.isPending || !email || !accessCode}
              >
                {authMutation.isPending ? 'Authenticating...' : 'Access Package'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoadingPackage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading package data...</p>
        </div>
      </div>
    );
  }

  // Package view
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {packageData?.name}
                </CardTitle>
                <div className="flex items-center space-x-4 mt-2">
                  <Badge variant="outline">{packageData?.packageNumber}</Badge>
                  <span className="text-sm text-gray-500">
                    for {packageData?.recipientCompany}
                  </span>
                </div>
              </div>
              <Button
                onClick={() => downloadMutation.mutate()}
                disabled={downloadMutation.isPending}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-2" />
                {downloadMutation.isPending ? 'Downloading...' : 'Download Package'}
              </Button>
            </div>
            
            {packageData?.description && (
              <p className="text-gray-600 mt-3">{packageData.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-500">Sent Date:</span>
                <p className="text-gray-900">{formatDate(packageData?.sentAt || '')}</p>
              </div>
              {packageData?.submissionDeadline && (
                <div>
                  <span className="font-medium text-gray-500">Deadline:</span>
                  <p className="text-gray-900">{formatDate(packageData.submissionDeadline)}</p>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-500">Total Files:</span>
                <p className="text-gray-900">{packageData?.items.length || 0}</p>
              </div>
              <div>
                <span className="font-medium text-gray-500">Expires:</span>
                <p className="text-gray-900">{formatDate(packageData?.expiresAt || '')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FolderOpen className="h-5 w-5 mr-2" />
              Package Contents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {packageData?.items && packageData.items.length > 0 ? (
              <div className="space-y-2">
                {packageData.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      {item.itemType === 'folder' ? (
                        <FolderOpen className="h-5 w-5 text-blue-500" />
                      ) : (
                        <FileText className="h-5 w-5 text-gray-500" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">{item.path}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {item.size && (
                        <p className="text-sm text-gray-500">{formatFileSize(item.size)}</p>
                      )}
                      {item.versionId && (
                        <p className="text-xs text-gray-400">V{item.versionId}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No files found in this package.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PackageView;